import apiClient, { setAuthFailureCallback } from "@service/ApiClient";
import { posthog } from "@service/posthogClient";
import React, {
	createContext,
	useState,
	useContext,
	ReactNode,
	useEffect,
	useCallback,
} from "react";
import { navigate } from "vike/client/router";
import { currentConfig } from "../config";

interface UserData {
	email: string;
	name: string;
	image: string;
	id: string;
	onboarding: number;
	credits: number;
	referral_code: string;
	streak_count?: number;
	userData: any;
	created_at: string;
	subscription_tier: string;
	is_subscribed: true;
	subscription_status: "active";
}

interface AuthContextType {
	user: UserData | null | undefined;
	isLoading: boolean;
	isAuthenticated: boolean;
	login: () => void;
	logout: () => void;
	checkAuth: () => void;
	refreshUser: () => Promise<void>; // For manual refresh after credit operations
	updateUser: (onboarding: number) => Promise<UserData | null | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<UserData | undefined | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const login = useCallback(async () => {
		const returnTo = encodeURIComponent(
			window.location.pathname + window.location.search,
		);
		window.location.href = `${currentConfig.apiBaseUrl}/api/auth/google?returnTo=${returnTo}`;
	}, []);

	// Handle auth failure from API interceptor
	const handleAuthFailure = useCallback(() => {
		setUser(null);
		setIsAuthenticated(false);
		setIsLoading(false);
		// Then redirect to login
		login();
	}, [login]);

	const checkAuth = async () => {
		try {
			const response = await apiClient.get("/api/auth/user");
			if (response.data) {
				try {
					posthog.identify(response.data.id, {
						email: response.data.email,
						name: response.data.name,
					});
				} catch {}

				setUser(response.data);
				setIsAuthenticated(true);

				// Check if user needs onboarding and redirect if not already on onboarding page
				if (
					response.data.onboarding === false &&
					typeof window !== "undefined"
				) {
					const currentPath = window.location.pathname;
					if (currentPath !== "/onboarding") {
						const returnTo = encodeURIComponent(
							currentPath + window.location.search,
						);
						navigate(`/onboarding?returnTo=${returnTo}`);
					}
				}
			}
			setIsLoading(false);
		} catch (error: any) {
			if (error?.response?.status === 403 && error?.response?.data?.redirect) {
				window.location.href = error.response.data.redirect;
				return;
			}
			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};

	const logout = () => {
		const callbackUrl = window.location.origin;
		window.location.href = `${currentConfig.apiBaseUrl}/api/auth/logout?callbackUrl=${encodeURIComponent(callbackUrl)}`;
	};

	useEffect(() => {
		setAuthFailureCallback(handleAuthFailure);

		setIsLoading(true);
		checkAuth()
			.then(() => {
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	}, [handleAuthFailure]);

	const updateUser = async (onboarding: number) => {
		const response = await apiClient.put(
			"/api/user/updateOnboarding",
			{
				onboarding: onboarding,
			},
			{
				headers: {
					"Content-Type": "application/json",
				},
			},
		);
		if (response.status !== 200) {
			throw new Error("Failed to update user data");
		}

		await checkAuth();
		return user;
	};

	// Refresh user data (for credit updates, etc.)
	const refreshUser = async () => {
		try {
			console.log("[AuthContext] Refreshing user data...");
			const response = await apiClient.get("/api/auth/user");
			if (response.data) {
				console.log("[AuthContext] User data refreshed:", response.data);
				setUser(response.data);
			} else {
				console.log("[AuthContext] No data in response");
			}
		} catch (error) {
			console.error("[AuthContext] Failed to refresh user data:", error);
		}
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				isLoading,
				isAuthenticated,
				login,
				logout,
				checkAuth,
				refreshUser,
				updateUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = (): AuthContextType => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
